import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { initApp } from './bootstrap';
import { isOidcConfigured } from './oidc';

function BootSplash({ label }: { label: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-dark text-brand-text-secondary gap-3">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const initStarted = useRef(false);
  const redirectStarted = useRef(false);
  const [appInitialized, setAppInitialized] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);

  useEffect(() => {
    const handleRefreshFailure = () => setRefreshFailed(true);
    window.addEventListener('mlp:auth-refresh-failed', handleRefreshFailure);
    return () => window.removeEventListener('mlp:auth-refresh-failed', handleRefreshFailure);
  }, []);

  useEffect(() => {
    if (initStarted.current) return;
    const canInit = !isOidcConfigured || auth.isAuthenticated;
    if (!canInit) return;
    initStarted.current = true;
    void initApp().finally(() => setAppInitialized(true));
  }, [auth.isAuthenticated]);

  useEffect(() => {
    if (!isOidcConfigured) return;
    if (auth.isLoading || auth.activeNavigator) return;
    if (auth.isAuthenticated) return;
    if (auth.error) return;
    if (redirectStarted.current) return;
    redirectStarted.current = true;
    void auth.signinRedirect();
  }, [auth]);

  if (!isOidcConfigured) {
    if (!appInitialized) return <BootSplash label="Loading..." />;
    return <>{children}</>;
  }
  if (auth.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-brand-dark text-brand-text-primary gap-4 px-4 text-center">
        <p className="text-lg font-semibold">登录失败</p>
        <p className="text-sm text-brand-text-secondary">{auth.error.message}</p>
        <button
          type="button"
          className="px-4 py-2 rounded bg-brand-orange text-white font-semibold"
          onClick={() => {
            redirectStarted.current = false;
            void auth.signinRedirect();
          }}
        >
          重新登录
        </button>
      </div>
    );
  }
  if (refreshFailed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-brand-dark text-brand-text-primary gap-4 px-4 text-center">
        <p className="text-lg font-semibold">登录状态需要恢复</p>
        <p className="text-sm text-brand-text-secondary">
          当前任务仍会保留。请先重试登录状态，失败后再重新登录。
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <button
            type="button"
            className="px-4 py-2 rounded border border-brand-border text-brand-text-primary"
            onClick={() => {
              void auth
                .signinSilent()
                .then(() => setRefreshFailed(false))
                .catch(() => setRefreshFailed(true));
            }}
          >
            重试
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded bg-brand-orange text-white font-semibold"
            onClick={() => {
              redirectStarted.current = false;
              void auth.signinRedirect();
            }}
          >
            重新登录
          </button>
        </div>
      </div>
    );
  }
  if (auth.isLoading || auth.activeNavigator) {
    return <BootSplash label="正在登录..." />;
  }
  if (!auth.isAuthenticated) {
    return <BootSplash label="正在跳转登录..." />;
  }
  if (!appInitialized) {
    return <BootSplash label="Loading..." />;
  }
  return <>{children}</>;
}
